/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  BuilderFormSchemaFactory,
  BuilderUserContext, FormRootSchema,
  FormState,
  FormStateSchema,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import { BuilderPermission, UoMDimension } from '@gh/builder-shared-data';
import { EnumDictionary, getDialogInitData, MaskedTypes, NumberTypes } from '@gh/common-shared';

import { FormMode } from '@gh/core-ui';
import { assign, find } from 'lodash';
import { UomDetails } from '../../resources/uom-details.model';

export interface DimensionTuple {
  predefined?: UoMDimension;
  user?: string;
}

@Injectable()
export class UomDetailsDialogFormState extends FormState<UomDetails> {
  private dimensions: EnumDictionary<UoMDimension>;

  constructor(schemaFactory: BuilderFormSchemaFactory,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private userContext: BuilderUserContext) {
    super(schemaFactory);

    const { dimensions } = getDialogInitData(dialogData);

    assign(this, { dimensions });
  }

  get submitValue(): UomDetails {
    const uom = this.group.value;

    const dimensionTuple1 = this.getDimensionTuple(uom.dimension1User);
    const dimensionTuple2 = this.getDimensionTuple(uom.dimension2User);
    const dimensionTuple3 = this.getDimensionTuple(uom.dimension3User);

    return {
      ...uom,
      dimension1: dimensionTuple1.predefined, dimension1User: dimensionTuple1.user,
      dimension2: dimensionTuple2.predefined, dimension2User: dimensionTuple2.user,
      dimension3: dimensionTuple3.predefined, dimension3User: dimensionTuple3.user,
      code: this.group.getRawValue().code,
    };
  }

  build(uom: UomDetails): void {
    this.initSchema({
      ...uom,
      dimension1User: this.findDimensionByTranslation(uom.dimension1, uom.dimension1User),
      dimension2User: this.findDimensionByTranslation(uom.dimension2, uom.dimension2User),
      dimension3User: this.findDimensionByTranslation(uom.dimension3, uom.dimension3User),
    });
  }

  protected buildSchema(uom: UomDetails, { root }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasPermission(BuilderPermission.Setup.Estimating.UoM.EDIT) ?
      FormMode.EditMode : FormMode.ViewMode;

    return root({
      mode: uom.id ? mode : FormMode.CreateMode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        code: { required: true, enabled: !uom.usedBySystem },
        name: void 0,
        usePieces: void 0,
        dimension1: void 0,
        dimension1User: void 0,
        dimension2: void 0,
        dimension2User: void 0,
        dimension3: void 0,
        dimension3User: void 0,
        format: { required: true, type: MaskedTypes.NumberFormat },
        roundUp: { required: true, type: NumberTypes.RoundUp },
      },
    });
  }

  private findDimensionByTranslation(dimension?: UoMDimension, dimensionUser?: string): Maybe<string> {
    return dimension ? this.dimensions.get(dimension) : dimensionUser;
  }

  private getDimensionTuple(dimensionUser?: string): DimensionTuple {
    const item = find(this.dimensions.seq(), { name: dimensionUser });
    return item ? { predefined: item.id } : { user: dimensionUser };
  }

}
